import type { DescentBridge } from './FileProfileStore';

/**
 * Crash journal (2026-07-27, after a whole-Deck freeze): a breadcrumb log
 * of coarse game actions appended to userData/journal.log via the Electron
 * bridge, so a hard freeze can be read back next boot ("what was it doing
 * when it died?"). Deck/Electron only — browser sessions no-op (devtools
 * exist there). Lines are BUFFERED and flushed at most once per second:
 * diagnostics must never reintroduce the write-per-press churn the
 * debounced profile store just removed. A hard freeze can eat the last
 * ≤1s of lines — the trail before it is the point.
 */
const JOURNAL_FLUSH_MS = 1000;

let bridge: DescentBridge | null = null;
let buf: string[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

/** Wire the journal to the Electron bridge (no-op without journalAppend). */
export function initJournal(b: DescentBridge | undefined): void {
  bridge = b?.journalAppend ? b : null;
  if (bridge && typeof window !== 'undefined') {
    window.addEventListener('pagehide', flushJournal);
    window.addEventListener('beforeunload', flushJournal);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushJournal();
    });
  }
  logJournal('--- session start ---');
}

/** Record one breadcrumb; cheap no-op in the browser. */
export function logJournal(event: string): void {
  if (!bridge) return;
  buf.push(`${new Date().toISOString()} ${event}`);
  if (timer !== null) return;
  timer = setTimeout(() => {
    timer = null;
    flushJournal();
  }, JOURNAL_FLUSH_MS);
}

export function flushJournal(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  if (!bridge || buf.length === 0) return;
  const text = `${buf.join('\n')}\n`;
  buf = [];
  try {
    bridge.journalAppend!(text);
  } catch { /* diagnostics must never hurt the game */ }
}
